/**
 * Chat Leads — everyone who has left their details on a website widget.
 *
 * The widget has asked visitors who they are since Phase 2 and asked for contact
 * details when nobody was available for longer than that. Neither answer was ever
 * read back by anything. This page is where both land, merged, so that collecting
 * an identity is a thing we do *for* a reason rather than a thing we do.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Download,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  UserPlus,
  Users,
} from 'lucide-react';
import { Badge, Button, Input, cn, useToast } from '@respectly/ui';
import type { ChatLeadDto } from '@respectly/shared-types';
import { useChatLeadsStore } from '../../stores/useChatLeadsStore';
import { useChatWidgetsStore } from '../../stores/useChatWidgetsStore';
import { AddToContactsDialog } from './AddToContactsDialog';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { relativeTime } from '../live-chats/SessionList';

const SOURCE_LABEL: Record<ChatLeadDto['source'], string> = {
  identified: 'Introduced themselves',
  captured: 'Left details offline',
  both: 'Both',
};

export function ChatLeads() {
  const { toast } = useToast();

  const leads = useChatLeadsStore((s) => s.leads);
  const truncated = useChatLeadsStore((s) => s.truncated);
  const brandId = useChatLeadsStore((s) => s.brandId);
  const query = useChatLeadsStore((s) => s.query);
  const loading = useChatLeadsStore((s) => s.loading);
  const exporting = useChatLeadsStore((s) => s.exporting);
  const error = useChatLeadsStore((s) => s.error);
  const load = useChatLeadsStore((s) => s.load);
  const setBrand = useChatLeadsStore((s) => s.setBrand);
  const setQuery = useChatLeadsStore((s) => s.setQuery);
  const openLead = useChatLeadsStore((s) => s.openLead);
  const exportCsv = useChatLeadsStore((s) => s.exportCsv);

  const brands = useChatWidgetsStore((s) => s.brands);
  const loadBrands = useChatWidgetsStore((s) => s.load);

  const [adding, setAdding] = useState<ChatLeadDto | null>(null);

  useEffect(() => {
    void load();
    void loadBrands();
  }, [load, loadBrands]);

  // Debounced search: the merge runs two indexed queries per keystroke otherwise.
  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [query, load]);

  const counts = useMemo(
    () => ({
      total: leads.length,
      asked: leads.filter((l) => l.askedForPerson).length,
    }),
    [leads]
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500/10 ring-1 ring-teal-500/20">
            <Users className="h-4.5 w-4.5 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">
              Chat Leads
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {counts.total} {counts.total === 1 ? 'person' : 'people'}
              {counts.asked > 0 && ` · ${counts.asked} asked for a person`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
            className="h-8 gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const ok = await exportCsv();
              if (!ok) {
                toast({ title: 'Could not export', variant: 'error' });
              }
            }}
            disabled={exporting || leads.length === 0}
            className="h-8 gap-1.5"
          >
            {exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder="Search by name or email"
            className="pl-9"
          />
        </div>
        <select
          value={brandId ?? ''}
          onChange={(e) => void setBrand(e.currentTarget.value || null)}
          className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
        >
          <option value="">All widgets</option>
          {brands.map((b) => (
            <option key={b.brandId} value={b.brandId}>
              {b.displayName}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {truncated && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          Showing the most recent leads only — the list merges two sources and
          cannot be paged. Narrow it with the search box or the widget filter to be
          sure you are seeing everyone.
        </div>
      )}

      {loading && leads.length === 0 ? (
        <p className="flex items-center gap-2 py-10 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading leads&hellip;
        </p>
      ) : leads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-14 text-center dark:border-slate-700">
          <Mail className="mx-auto h-6 w-6 text-slate-300 dark:text-slate-600" />
          <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            Nobody has left their details yet
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            A visitor lands here when they introduce themselves, or when they leave
            contact details because nobody was available.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="hidden px-4 py-2 font-medium md:table-cell">Widget</th>
                <th className="hidden px-4 py-2 font-medium lg:table-cell">How</th>
                <th className="px-4 py-2 font-medium">Last seen</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {leads.map((lead) => (
                <tr
                  key={lead.id}
                  className="cursor-pointer bg-white transition-colors hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                  onClick={() => void openLead(lead.id)}
                >
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {lead.name ?? '—'}
                    </span>
                    {lead.askedForPerson && (
                      <Badge variant="secondary" className="ml-2 text-[10px]">
                        asked for a person
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{lead.email}</td>
                  <td className="hidden px-4 py-2.5 text-slate-500 dark:text-slate-400 md:table-cell">
                    {lead.brandName}
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-500 dark:text-slate-400 lg:table-cell">
                    {SOURCE_LABEL[lead.source]}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400">
                    {relativeTime(lead.lastSeenAt)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1.5 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAdding(lead);
                      }}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Add to contacts
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LeadDetailDrawer onAddToContacts={setAdding} />
      <AddToContactsDialog lead={adding} open={Boolean(adding)} onClose={() => setAdding(null)} />
    </div>
  );
}
